import React from 'react';
import { toast } from 'react-toastify';
import { skipToken } from '@reduxjs/toolkit/query';
import { trackError, toTrackedError } from '@src/core/sentry';
import { useSelectedFleetDrone } from '@src/features/fleet/pages/drones-page/hooks';
import {
  useGetDroneMaintenanceQuery,
  useAdjustDroneMaintenanceMutation,
  useResetPartDroneMutation,
} from '@src/features/fleet/redux/api';
import type { DroneMaintenancePartId } from '@src/features/fleet/redux/types';
import { isInitialLoad } from '@src/features/fleet/pages/drones-page/utils/query-state';
import { ADJUST_TYPE_TO_API, AdjustType, RuntimeItem } from '../types';
import { getMotorRuntimeItems, getPropellerRuntimeItems, SECONDS_PER_HOUR } from '../utils/runtime-items';

export function useMaintenance() {
  const { droneId } = useSelectedFleetDrone();
  const [open, setOpen] = React.useState(false);
  const [adjustType, setAdjustType] = React.useState<AdjustType | null>(null);
  const [initialValue, setInitialValue] = React.useState<number>(0);
  const [adjustingPart, setAdjustingPart] = React.useState<DroneMaintenancePartId | null>(null);

  const {
    currentData: maintenance,
    isLoading,
    isFetching,
  } = useGetDroneMaintenanceQuery(droneId ?? skipToken);
  const [adjustDroneMaintenance] = useAdjustDroneMaintenanceMutation();
  const [resetPartDrone] = useResetPartDroneMutation();

  const onClose = () => {
    setOpen(false);
    setAdjustType(null);
    setAdjustingPart(null);
  };

  const handleAdjustOptionClick = React.useCallback((optionId: AdjustType, item: RuntimeItem) => {
    setAdjustType(optionId);
    setInitialValue(optionId === 'runtime' ? item.currentHours : item.limitHours);
    setAdjustingPart(item.partId);
    setOpen(true);
  }, []);

  const handleReplaceClick = React.useCallback(
    async (item: RuntimeItem) => {
      if (droneId == null) return;
      try {
        await resetPartDrone({ id: droneId, part: item.partId }).unwrap();
        toast.success('Maintenance part has been reset');
      } catch (err: unknown) {
        trackError(toTrackedError(err), { code: 'FE_0021', extra: { flow: 'resetDroneMaintenancePart' } });
        toast.error('Failed to reset maintenance part');
      }
    },
    [droneId, resetPartDrone]
  );

  const handleSave = React.useCallback(
    async (valueHours: number) => {
      if (adjustType == null || adjustingPart == null || droneId == null) return;
      const type = ADJUST_TYPE_TO_API[adjustType];
      const valueSeconds = Math.round(valueHours * SECONDS_PER_HOUR);
      try {
        await adjustDroneMaintenance({
          id: droneId,
          part: adjustingPart,
          type,
          value: valueSeconds,
        }).unwrap();
        toast.success('Maintenance has been updated');
        onClose();
      } catch (err: unknown) {
        trackError(toTrackedError(err), { code: 'FE_0021', extra: { flow: 'adjustDroneMaintenance' } });
        toast.error('Failed to update maintenance');
      }
    },
    [adjustType, adjustingPart, droneId, adjustDroneMaintenance]
  );

  const motorItems = React.useMemo((): RuntimeItem[] => {
    return getMotorRuntimeItems(maintenance);
  }, [maintenance]);

  const propellerItems = React.useMemo((): RuntimeItem[] => {
    return getPropellerRuntimeItems(maintenance);
  }, [maintenance]);

  return {
    motorItems,
    propellerItems,
    isLoading: isInitialLoad({ isLoading, isFetching }, maintenance),
    adjustPopup: {
      open,
      type: adjustType,
      initialValue,
      onClose,
      onSave: handleSave,
    },
    handleAdjustOptionClick,
    handleReplaceClick,
  };
}
